import { z } from 'zod';
import type { OrderStatus, ShipmentStatus } from './shipment-status';

const httpsUrl = z.url({ protocol: /^https$/, message: 'Use a full https:// link.' }).max(500);
const optionalHttpsUrl = z.union([httpsUrl, z.literal('')]).optional();
const hexColor = z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Use a hex colour like #0F766E.');

export const SOCIAL_NETWORKS = [
  'instagram',
  'facebook',
  'x',
  'youtube',
  'tiktok',
  'website',
] as const;

export const trackingPageSettingsSchema = z.object({
  storeName: z.string().trim().min(1).max(80),
  logoUrl: z
    .union([httpsUrl, z.string().regex(/^\/api\/public\/assets\/[0-9a-f-]{36}$/), z.literal('')])
    .optional(),
  primaryColor: hexColor,
  accentColor: hexColor,
  headerLinks: z
    .array(z.object({ label: z.string().trim().min(1).max(30), url: httpsUrl }))
    .max(5, 'Up to 5 header links.'),
  footerText: z.string().trim().max(200).optional(),
  customMessage: z.string().trim().max(500).optional(),
  socialLinks: z.object(
    Object.fromEntries(SOCIAL_NETWORKS.map((n) => [n, optionalHttpsUrl])) as Record<
      (typeof SOCIAL_NETWORKS)[number],
      typeof optionalHttpsUrl
    >,
  ),
  supportEmail: z.union([z.email('Enter a valid email address.'), z.literal('')]).optional(),
});
export type TrackingPageSettingsInput = z.infer<typeof trackingPageSettingsSchema>;

export const publicTrackSchema = z.union([
  z.object({
    orderNumber: z.string().trim().min(1, 'Enter your order number.').max(40),
    email: z.string().trim().toLowerCase().pipe(z.email('Enter the email you used to order.')),
  }),
  z.object({
    trackingNumber: z
      .string()
      .trim()
      .min(4, 'Enter your tracking number.')
      .max(60)
      .regex(/^[A-Za-z0-9-]+$/, 'Tracking numbers contain letters, digits and dashes only.'),
  }),
  z.object({ token: z.string().min(20).max(200) }),
]);
export type PublicTrackInput = z.infer<typeof publicTrackSchema>;

export interface PublicBranding {
  slug: string;
  storeName: string;
  logoUrl: string | null;
  primaryColor: string;
  accentColor: string;
  headerLinks: { label: string; url: string }[];
  footerText: string | null;
  customMessage: string | null;
  socialLinks: Partial<Record<(typeof SOCIAL_NETWORKS)[number], string>>;
  supportEmail: string | null;
  /** False on plans without custom branding: TrackFlow's default look is used. */
  branded: boolean;
}

export interface TimelineStep {
  key: 'confirmed' | 'packed' | 'shipped' | 'in_transit' | 'out_for_delivery' | 'delivered';
  label: string;
  state: 'done' | 'current' | 'upcoming';
  at: string | null;
}

export interface PublicShipment {
  carrier: string | null;
  trackingNumber: string | null;
  trackingUrl: string | null;
  status: ShipmentStatus;
  statusLabel: string;
  estimatedDeliveryAt: string | null;
  deliveredAt: string | null;
  lastLocation: string | null;
  /** False when TrackFlow isn't receiving live updates (unsupported carrier or plan limit). */
  liveTracking: boolean;
  timeline: TimelineStep[];
  events: {
    occurredAt: string;
    status: ShipmentStatus;
    description: string;
    location: string | null;
  }[];
}

export interface PublicTrackingResult {
  /** "full" after order number + email (or a signed link); "limited" for tracking number only. */
  view: 'full' | 'limited';
  order: {
    orderNumber: string;
    orderedAt: string;
    status: OrderStatus;
    customerFirstName: string | null;
    destination: { city: string | null; region: string | null; country: string | null } | null;
    items: {
      title: string;
      variantTitle: string | null;
      quantity: number;
      imageUrl: string | null;
    }[];
  } | null;
  shipments: PublicShipment[];
}
